import { Avatar, Icons, Spinner } from "@dms/ui";
import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { ADMIN_ROLES, NAV_ITEMS, navSectionsForRoles } from "../nav";

const SIDEBAR_W = "var(--layout-sidebar-w)";
const SIDEBAR_COLLAPSED_W = "var(--layout-sidebar-collapsed-w)";

function pageTitle(pathname: string): string {
  if (pathname === "/" || pathname === "/admin") return "Адміністрування";
  if (pathname.startsWith("/settings")) return "Налаштування";
  const item = NAV_ITEMS.find((i) => pathname.startsWith(i.path));
  return item?.label ?? "Адміністрування";
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  const handleLogout = async () => {
    setUserMenu(false);
    await logout();
    navigate("/login", { replace: true });
  };

  const fullName = user?.full_name ?? "Адміністратор";
  const role = user?.roles?.[0] ?? "";
  const title = pageTitle(location.pathname);
  const navSections = navSectionsForRoles(user?.roles ?? []);
  const canAccessAdminSettings = ADMIN_ROLES.some((role) =>
    user?.roles.includes(role),
  );

  return (
    <div className="admin-shell" style={{ display: "flex", height: "100vh", overflow: "hidden", background: "var(--color-surface-default)" }}>
      <button
        type="button"
        aria-label="Закрити головне меню"
        className={`admin-drawer-backdrop${mobileOpen ? " is-visible" : ""}`}
        onClick={() => setMobileOpen(false)}
      />
      <aside
        className={`admin-sidebar${mobileOpen ? " is-mobile-open" : ""}`}
        style={{
          width: collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_W,
          flex: "none",
          background: "var(--color-surface-sidebar)",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          transition: "width var(--duration-base) var(--ease-standard)",
          height: "100%",
        }}
      >
        <div
          style={{
            height: "var(--layout-header-h)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: collapsed ? "0 14px" : "0 18px",
            borderBottom: "1px solid var(--color-border-on-dark)",
            flex: "none",
          }}
        >
          <img
            className="admin-sidebar__logo"
            src={collapsed ? "/brand/clearflow-mark-white.png" : "/brand/clearflow-logo-horizontal-white.png"}
            alt="ClearFlow"
            style={{ height: 26, objectFit: "contain" }}
          />
        </div>

        <nav className="admin-sidebar__nav" aria-label="Головна навігація" style={{ flex: 1, padding: "10px 10px", display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
          {navSections.map((section, si) => (
            <div key={si} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {section.heading &&
                (collapsed ? (
                  <div style={{ height: 1, background: "var(--color-border-on-dark)", margin: "10px 6px 8px" }} />
                ) : (
                  <div
                    style={{
                      padding: "14px 12px 4px",
                      fontSize: 10.5,
                      fontWeight: "var(--fw-semibold)",
                      letterSpacing: "var(--ls-caps)",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.42)",
                    }}
                  >
                    {section.heading}
                  </div>
                ))}
              {section.items.map((item) => {
                const Icon = Icons[item.icon];
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    title={item.label}
                    className="admin-sidebar__link"
                    onClick={() => setMobileOpen(false)}
                    style={({ isActive }) => ({
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: collapsed ? "10px 0" : "9px 12px",
                      justifyContent: collapsed ? "center" : "flex-start",
                      borderRadius: "var(--radius-md)",
                      textDecoration: "none",
                      background: isActive ? "rgba(255,255,255,0.14)" : "transparent",
                      color: isActive ? "#fff" : "rgba(255,255,255,0.72)",
                      fontSize: "var(--fs-nav)",
                      fontWeight: isActive ? "var(--fw-semibold)" : "var(--fw-medium)",
                      position: "relative",
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span style={{ position: "absolute", left: 0, top: 8, bottom: 8, width: 3, background: "var(--teal-300)", borderRadius: "0 2px 2px 0" }} />
                        )}
                        <Icon size={20} />
                        {!collapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div style={{ position: "relative", borderTop: "1px solid var(--color-border-on-dark)", flex: "none" }}>
          {userMenu && (
            <>
              <div onClick={() => setUserMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 1 }} />
              <div
                className="admin-user-menu"
                role="menu"
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  left: 10,
                  right: 10,
                  background: "#fff",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-border-default)",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-popover)",
                  padding: 6,
                  zIndex: 2,
                }}
              >
                {canAccessAdminSettings && (
                  <RouterLink
                    role="menuitem"
                    to="/admin/settings"
                    onClick={() => setUserMenu(false)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: "var(--radius-md)", color: "var(--color-text-primary)", textDecoration: "none", fontSize: "var(--fs-body-sm)" }}
                  >
                    <Icons.settings size={17} /> Системні налаштування
                  </RouterLink>
                )}
                <RouterLink
                  role="menuitem"
                  to="/admin/change-password"
                  onClick={() => setUserMenu(false)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: "var(--radius-md)", color: "var(--color-text-primary)", textDecoration: "none", fontSize: "var(--fs-body-sm)" }}
                >
                  <Icons.edit size={17} /> Змінити пароль
                </RouterLink>
                <button
                  role="menuitem"
                  type="button"
                  onClick={() => void handleLogout()}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "none", background: "transparent", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: "var(--fs-body-sm)", color: "var(--color-status-error)", fontFamily: "var(--font-sans)", textAlign: "left" }}
                >
                  <Icons.lock size={17} /> Вийти
                </button>
              </div>
            </>
          )}
          <button
            className="admin-user-menu__trigger"
            type="button"
            aria-label="Меню користувача"
            onClick={() => setUserMenu((v) => !v)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: collapsed ? "12px 0" : "12px 16px",
              justifyContent: collapsed ? "center" : "flex-start",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#fff",
              fontFamily: "var(--font-sans)",
              textAlign: "left",
            }}
          >
            <Avatar name={fullName} size={collapsed ? 28 : 34} />
            {!collapsed && (
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "var(--fs-body-sm)", fontWeight: "var(--fw-semibold)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{fullName}</span>
                {role && (
                  <span style={{ display: "block", fontSize: "var(--fs-meta)", color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{role}</span>
                )}
              </span>
            )}
            {!collapsed && <Icons.settings size={16} style={{ color: "rgba(255,255,255,0.6)" }} />}
          </button>
        </div>

        <button
          className="admin-sidebar__collapse"
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Розгорнути меню" : "Згорнути меню"}
          style={{ height: 40, flex: "none", border: "none", borderTop: "1px solid var(--color-border-on-dark)", background: "transparent", color: "rgba(255,255,255,0.6)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: "var(--fs-body-sm)", fontFamily: "var(--font-sans)" }}
        >
          {collapsed ? <Icons.chevronRight size={18} /> : <Icons.chevronLeft size={18} />}
          {!collapsed && <span>Згорнути</span>}
        </button>
      </aside>

      <div className="admin-shell__body" style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          className="admin-header"
          style={{
            height: "var(--layout-header-h)",
            flex: "none",
            background: "var(--color-surface-header)",
            borderBottom: "1px solid var(--color-border-default)",
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "0 20px",
          }}
        >
          <button
            type="button"
            className="admin-header__menu"
            aria-label="Відкрити головне меню"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
          >
            <Icons.menu size={21} />
          </button>
          <h1 className="admin-header__title" style={{ flex: 1, minWidth: 0, margin: 0, fontSize: "var(--fs-h3)", lineHeight: "var(--lh-h3)", fontWeight: "var(--fw-semibold)", color: "var(--color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {title}
          </h1>
          <span className="admin-header__context" style={{ fontSize: "var(--fs-meta)", color: "var(--color-text-tertiary)", letterSpacing: "var(--ls-caps)", textTransform: "uppercase" }}>
            Адміністрування
          </span>
        </header>

        <main className="admin-main" style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          <div className="admin-content" style={{ maxWidth: "var(--layout-content-max)", margin: "0 auto" }}>
            {/* TD-0047: Suspense навколо Outlet для lazy route-chunks (shell лишається). */}
            <Suspense
              fallback={
                <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                  <Spinner />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
